import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { reportGroups, reports, conclusionMeta } from "@/mocks/reports";

export default function ReportSection() {
  const [active, setActive] = useState("all");

  const list = useMemo(
    () => (active === "all" ? reports : reports.filter((r) => r.group === active)),
    [active]
  );

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "最新核查结论",
    url: "https://readdy.ai/",
    description: "求真核查站对热门在线黑料的真假鉴定与事实核查结论汇总。",
    hasPart: list.map((r) => ({
      "@type": "Article",
      headline: r.title,
      datePublished: r.date,
      articleBody: r.summary,
    })),
  };

  return (
    <section id="reports" className="bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="最新核查结论"
          title="热点追踪，结论随时更新"
          desc="每条结论都附可信度评分与证据数量，点击可对照出处复盘完整核查过程。"
        />

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <button
            onClick={() => setActive("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              active === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300"
            }`}
          >
            全部议题
          </button>
          {reportGroups.map((g) => (
            <button
              key={g.id}
              onClick={() => setActive(g.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                active === g.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {list.map((r) => {
            const meta = conclusionMeta[r.conclusion];
            return (
              <article
                key={r.title}
                className="group rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 hover:border-primary-300 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <span
                    className={`inline-flex items-center self-start gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${meta.cls}`}
                  >
                    <i className="ri-flag-2-line"></i>
                    {r.conclusion}
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-600">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-folder-3-line"></i>
                        {r.group}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {r.date}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-file-list-3-line"></i>
                        {r.evidence} 项证据
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-user-heart-line"></i>
                        {r.sources} 个信源
                      </span>
                    </div>
                    <h3 className="mt-2 font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                      {r.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                      {r.summary}
                    </p>
                  </div>

                  <div className="lg:w-44 shrink-0 lg:pt-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-600">可信度</span>
                      <span className="font-bold text-foreground-900">{r.confidence}%</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500"
                        style={{ width: `${r.confidence}%` }}
                      ></div>
                    </div>
                    <a
                      href="#about"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
                    >
                      查看完整复盘
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </div>
    </section>
  );
}